import * as anchor from "@coral-xyz/anchor";
import { getAssociatedTokenAddressSync } from "@solana/spl-token";
import { Connection, Keypair, PublicKey } from "@solana/web3.js";
import bs58 from "bs58";
import CryptoJS from "crypto-js";
import {
    IP_INFO_API_KEY,
    RPC_URL,
} from "./constants";

export function formatDateTime(dateTimeString: string): string {
  const date = new Date(dateTimeString);
  // const year = date.getFullYear().toString().slice(-2);
  const month = ("0" + (date.getMonth() + 1)).slice(-2);
  const day = ("0" + date.getDate()).slice(-2);
  const hours = ("0" + date.getHours()).slice(-2);
  const minutes = ("0" + date.getMinutes()).slice(-2);
  return `${month}/${day} ${hours}:${minutes}`;
}

export const copyText = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    console.error("Failed to copy:", err);
    return false;
  }
};

export function shortenAddress(address: string, length = 6) {
  try {
    return address.slice(0, length) + "..." + address.slice(-length);
  } catch (e) {
    return "";
  }
}

export function generateRandomString(length: number) {
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  const charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
}

export function displayWithNonZeroDecimals(
  value: number | string | undefined,
  decimalsAfterNonZero = 2
): string | undefined {
  if (value === undefined) {
    return undefined;
  }
  const numStr = value.toString();
  const [integerPart, decimalPart] = numStr.split(".");

  if (!decimalPart) {
    // No decimal part
    return numStr;
  }

  // Find the position of the first non-zero digit in the decimal part
  let firstNonZeroIndex = -1;
  for (let i = 0; i < decimalPart.length; i++) {
    if (decimalPart[i] !== "0") {
      firstNonZeroIndex = i;
      break;
    }
  }

  if (firstNonZeroIndex === -1) {
    // All decimals are zero
    return integerPart;
  }

  // Calculate the length to display
  const endIndex = firstNonZeroIndex + decimalsAfterNonZero + 1; // +1 to include the first non-zero
  const truncatedDecimalPart = decimalPart.slice(0, endIndex);
  const significantDecimalPart = truncatedDecimalPart.replace(/0+$/, "");

  // Combine the integer and truncated decimal parts
  return `${integerPart}.${significantDecimalPart}`;
}

const passphrase = "forencryptkey";

export function encryptKey(key: string | undefined) {
  if (key) {
    return CryptoJS.AES.encrypt(key, passphrase).toString();
  } else {
    return "";
  }
}

export function decryptKey(encrypted: string | undefined) {
  if (encrypted) {
    return CryptoJS.AES.decrypt(encrypted, passphrase).toString(
      CryptoJS.enc.Utf8
    );
  } else {
    return "";
  }
}

export function createKeypairFromString(
  base58PrivateKey: string
): Keypair | null {
  try {
    const secretKey = bs58.decode(base58PrivateKey);

    // Ensure the length of the secretKey is 64 bytes
    if (secretKey.length !== 64) {
      throw new Error("Invalid secret key length. Expected 64 bytes.");
    }

    const keypair = Keypair.fromSecretKey(secretKey);
    return keypair;
  } catch (error) {
    console.error("Failed to create Keypair:");
    return null;
  }
}

export function isValidSolanaAddress(address: string): boolean {
  try {
    const publicKey = new anchor.web3.PublicKey(address);
    return publicKey.toBase58() === address;
  } catch (error) {
    return false;
  }
}

export const ENCRYPTION_KEY = new Uint8Array([
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22,
  23, 24, 25, 26, 27, 28, 29, 30, 31, 32,
]);

export function encryptToSignup(nftMint: PublicKey): PublicKey {
  const mintBytes = nftMint.toBytes();
  const result = new Uint8Array(32);

  for (let i = 0; i < 32; i++) {
    result[i] = mintBytes[i] ^ ENCRYPTION_KEY[i];
  }

  return new PublicKey(result);
}

export function decryptToMint(signupAddress: PublicKey): PublicKey {
  const signupBytes = signupAddress.toBytes();
  const result = new Uint8Array(32);

  for (let i = 0; i < 32; i++) {
    result[i] = signupBytes[i] ^ ENCRYPTION_KEY[i];
  }

  return new PublicKey(result);
}

export const getConnection = (): Connection => {
  return new anchor.web3.Connection(RPC_URL, "confirmed");
};



export const getTokenBalance = async (
  owner: string,
  mint: string
): Promise<number> => {
  try {
    if (owner === "" || mint === "") {
      return 0;
    }
    const connection = getConnection();
    const tokenAccount = getAssociatedTokenAddressSync(
      new PublicKey(mint),
      new PublicKey(owner),
      false
    );
    const balance = await connection.getTokenAccountBalance(tokenAccount);
    return balance.value.uiAmount || 0;
  } catch (error: any) {
    console.log("Error getting token balance:", error.message);
    return 0;
  }
};

export async function checkIfIpIsInUS() {
  try {
    const response = await fetch(
      `https://ipinfo.io/json?token=${IP_INFO_API_KEY}`
    );
    const data = await response.json();

    if (data.country === "US") {
      return true;
    } else {
      return false;
    }
  } catch (error) {
    console.error("Error checking if IP is in US:", error);
    return false;
  }
}
